import {HStack} from 'platform/foundation';
import styled from 'styled-components';

import {PointerEvent, ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {COMPARISON_HEADER_ATTRIBUTE} from '../constants/COMPARISON_HEADER_ATTRIBUTE';
import {COMPARISON_HIGHLIGHT_ATTRIBUTE} from '../constants/COMPARISON_HIGHLIGHT_ATTRIBUTE';
import {COMPARISON_HOVERED_ROW_ATTRIBUTE} from '../constants/COMPARISON_HOVERED_ROW_ATTRIBUTE';
import {COMPARISON_ROW_ATTRIBUTE} from '../constants/COMPARISON_ROW_ATTRIBUTE';
import {ComparisonHoverContext} from '../contexts/ComparisonHoverContext';
import {ComparisonTestIdContext} from '../contexts/ComparisonTestIdContext';

interface ComparisonHoverSurfaceProps extends TestIdProps {
  children: ReactNode;
  hasUnifiedSurfaces: boolean;
}

function setHoveredRow(surface: HTMLDivElement, rowKey?: string) {
  // The attribute remembers the previous row so moving inside it avoids scanning the matrix.
  const previousRowKey = surface.getAttribute(COMPARISON_HOVERED_ROW_ATTRIBUTE) ?? undefined;

  if (previousRowKey === rowKey) {
    return;
  }

  surface.querySelectorAll(`[${COMPARISON_ROW_ATTRIBUTE}]`).forEach((cell) => {
    if (cell.getAttribute(COMPARISON_ROW_ATTRIBUTE) === previousRowKey) {
      cell.setAttribute(COMPARISON_HIGHLIGHT_ATTRIBUTE, 'false');
    }
    if (cell.getAttribute(COMPARISON_ROW_ATTRIBUTE) === rowKey) {
      cell.setAttribute(COMPARISON_HIGHLIGHT_ATTRIBUTE, 'true');
    }
  });

  if (isNotNil(rowKey)) {
    surface.setAttribute(COMPARISON_HOVERED_ROW_ATTRIBUTE, rowKey);
  } else {
    surface.removeAttribute(COMPARISON_HOVERED_ROW_ATTRIBUTE);
  }
}

function handlePointerOver(event: PointerEvent<HTMLDivElement>) {
  if (event.pointerType === 'touch') {
    return;
  }

  const target = event.target;
  const cell = target instanceof Element ? target.closest(`[${COMPARISON_ROW_ATTRIBUTE}]`) : null;
  if (isNotNil(cell) && event.currentTarget.contains(cell)) {
    setHoveredRow(event.currentTarget, cell.getAttribute(COMPARISON_ROW_ATTRIBUTE) ?? undefined);
  } else if (
    target instanceof Element &&
    isNotNil(target.closest(`[${COMPARISON_HEADER_ATTRIBUTE}]`))
  ) {
    setHoveredRow(event.currentTarget);
  }
}

export function ComparisonHoverSurface(props: ComparisonHoverSurfaceProps) {
  return (
    <ComparisonHoverContext.Provider value={props.hasUnifiedSurfaces}>
      <ComparisonTestIdContext.Provider value={props['data-testid']}>
        {props.hasUnifiedSurfaces ? (
          <Surface
            onPointerOver={handlePointerOver}
            onPointerLeave={(event) => setHoveredRow(event.currentTarget)}
            data-testid={suffixTestId('comparison-hover-surface', props)}
          >
            {props.children}
          </Surface>
        ) : (
          <HStack>{props.children}</HStack>
        )}
      </ComparisonTestIdContext.Provider>
    </ComparisonHoverContext.Provider>
  );
}

const Surface = styled.div`
  display: flex;
`;
